// Knowledge base search hook — wraps kb-search edge function
import { useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { SearchResult } from '@/types/knowledge'

interface KBSearchParams {
  query: string
  d365Version?: string
  d365Module?: string
  expertiseDomain?: string
  maxResults?: number
  confidenceThreshold?: number
}

interface KBSearchResponse {
  results: SearchResult[]
  totalMatches: number
  executionTimeMs: number
}

export function useKBSearch() {
  const searchMutation = useMutation({
    mutationFn: async (params: KBSearchParams): Promise<KBSearchResponse> => {
      const { data, error } = await supabase.functions.invoke('kb-search', {
        body: {
          query: params.query,
          filters: {
            d365Version: params.d365Version,
            modules: params.d365Module ? [params.d365Module] : undefined,
            expertiseDomain: params.expertiseDomain || undefined,
          },
          options: {
            maxResults: params.maxResults ?? 10,
            confidenceThreshold: params.confidenceThreshold ?? 0.70,
          },
        },
      })

      if (error) throw new Error(error.message || 'KB search failed')
      return data as KBSearchResponse
    },
  })

  return {
    search: searchMutation.mutateAsync,
    results: searchMutation.data?.results ?? [],
    metadata: searchMutation.data
      ? { totalMatches: searchMutation.data.totalMatches, executionTimeMs: searchMutation.data.executionTimeMs }
      : undefined,
    isSearching: searchMutation.isPending,
    error: searchMutation.error,
    reset: searchMutation.reset,
  }
}
